// Earth world Y-range from scale (ERT-5). PURE (ENG-1). An Earth world's vertical
// extent is derived from its metres-per-block scale so the same real elevation span
// (`EARTH.RANGE_M`) always fits: at the 0.9144 m default this is the −640..10 111 /
// 672-section column (Dead-Sea shore to Everest with headroom). Fine scales that
// would need more than `EARTH.MAX_SECTIONS` clamp — elevations then plateau at the
// bound and the world is badged "elevation clamped" (E-41). The Int16 heightmap
// bound (TAL-5) is honoured too: a surface-Y can never wrap.
import { EARTH } from '../config';
import { makeRange, type YRange } from '../engine/section';

/** Int16 heightmap bounds (TAL-5): a column's surface-Y must stay in this range. */
const I16_MIN = -32768;
const I16_MAX = 32767;

/** The per-world block Y-range for an Earth world at `scaleM` metres/block (ERT-5).
 *  Section-aligned; `EARTH.RANGE_M` mapped through the scale, clamped to
 *  `EARTH.MAX_SECTIONS` and the Int16 bound (E-41 / TAL-5). Default 0.9144 →
 *  minY −640, height 10 752 (672 sections), maxY 10 111. */
export function earthRange(scaleM: number): YRange {
  const s = scaleM > 0 ? scaleM : EARTH.DEFAULT.scaleM;
  // Floor of the datum to a whole section below the lowest elevation.
  let minY = Math.floor(EARTH.RANGE_M.MIN / s / 16) * 16;
  if (minY < I16_MIN) minY = Math.ceil(I16_MIN / 16) * 16; // section-aligned Int16 floor
  // Whole sections spanning the elevation range, clamped for very fine scales.
  const spanBlocks = (EARTH.RANGE_M.MAX - EARTH.RANGE_M.MIN) / s;
  let sections = Math.max(1, Math.min(EARTH.MAX_SECTIONS, Math.floor(spanBlocks / 16)));
  // Never let the top wrap Int16 (TAL-5): shrink the column if the ceiling is hit.
  while (sections > 1 && minY + sections * 16 - 1 > I16_MAX) sections--;
  return makeRange(minY, sections * 16);
}

/** True when the world's elevation range is clamped (fine scale hit `MAX_SECTIONS`,
 *  or the Int16 ceiling) so the tallest ground plateaus at the bound (E-41). */
export function earthRangeClamped(scaleM: number): boolean {
  const s = scaleM > 0 ? scaleM : EARTH.DEFAULT.scaleM;
  const wantSections = Math.floor((EARTH.RANGE_M.MAX - EARTH.RANGE_M.MIN) / s / 16);
  return wantSections > earthRange(s).sectionsPerColumn;
}
